import { useEffect, useState, useContext, useCallback } from "react"
import { UserContext } from "../../../context/userContext"

//Styles
import docSignStyles from "../../../styles/documentSigning/DocSign.filters.module.css"
import docSignReqStyles from "../../../styles/documentSigning/DocSign.requests.filter.module.css"
import formStyles from "../../../styles/Form-Inputs.module.css"

//primeReact
import { Calendar } from "primereact/calendar"
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { MultiSelect } from "primereact/multiselect"
import { Tooltip } from "primereact/tooltip"

const RequestsFilters = ({
  filters,
  sortOptions,
  myStaffProfile,
  onChangeFilters,
  onChangeSortField,
  userOptions,
  isLoading,
}) => {
  const userCtx = useContext(UserContext)

  const sortDirections = [
    { value: "Descending", icon: "arrow_downward" },
    { value: "Ascending", icon: "arrow_upward" },
  ]

  const [dateFilter, setDateFilter] = useState(null)

  const getAllParticipantOption = useCallback(() => {
    return {
      name: "All participants",
      participant_id: "ALL",
    }
  }, [])
  const allParticipants = getAllParticipantOption()
  const maxFilterSelection = 50

  const [searchPhrase, setSearchPhrase] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [staffFilter, setStaffFilter] = useState([myStaffProfile])
  const [participantFilter, setParticipantFilter] = useState([
    getAllParticipantOption(),
  ])
  const [sortField, setSortField] = useState(sortOptions[0])
  const [sortDirection, setSortDirection] = useState(sortDirections[0])

  const [staffOptions, setStaffOptions] = useState([myStaffProfile])
  const [participantOptions, setParticipantOptions] = useState([
    allParticipants,
  ])
  const statusOptions = [
    { name: "All", id: "all" },
    { name: "Awaiting Signature", id: "awaiting_signature" },
    { name: "Signed", id: "signed" },
    { name: "Declined", id: "declined" },
  ]

  useEffect(() => {
    if (filters.sort !== sortField) {
      setSortField(filters.sort)
    }

    if (filters.sortDirection !== sortDirection.value) {
      if (sortDirection.value === sortDirections[0].value) {
        setSortDirection(sortDirections[1])
      } else {
        setSortDirection(sortDirections[0])
      }
    }

    //Note: Disable exhaustive deps due to only wanting to fire this event when filters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  useEffect(() => {
    userOptions.forEach((list) => {
      switch (list.name) {
        case "Staff":
          setStaffOptions([
            myStaffProfile,
            ...list.items.filter(
              (staff) => staff.signerId !== myStaffProfile.signerId
            ),
          ])
          break
        case "Participants":
          const allParticipants = getAllParticipantOption()
          setParticipantOptions([allParticipants, ...list.items])
          break
        default:
          break
      }
    })
  }, [
    getAllParticipantOption,
    myStaffProfile,
    userCtx.session.user.staff_alias,
    userCtx.session.user.staff_id,
    userCtx.session.user.staff_name_first,
    userCtx.session.user.staff_name_last,
    userOptions,
  ])

  useEffect(() => {
    if (filters.status !== statusFilter) {
      setStatusFilter(filters.status)
    }

    if (filters.dateCreated !== dateFilter) {
      setDateFilter(filters.dateCreated)
    }

    if (filters.search !== searchPhrase) {
      setSearchPhrase(!filters.search ? "" : filters.search)
    }

    if (filters.staff !== staffFilter) {
      setStaffFilter(filters.staff)
    }

    if (filters.participants !== participantFilter) {
      if (filters.participants.length === 0) {
        setParticipantFilter([allParticipants])
      } else {
        setParticipantFilter(filters.participants)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  return (
    <div className={docSignReqStyles.filterGridContainer}>
      <Tooltip target={".tt-ds-requests-filter"} />
      <div className={docSignReqStyles.filterItemDate}>
        <div className={docSignReqStyles.label}>Filter by Date Requested: </div>
        <div className={docSignReqStyles.dateInput}>
          <Calendar
            className={docSignReqStyles.calendar}
            value={dateFilter}
            onChange={(e) => setDateFilter(e.value)}
            selectionMode="range"
            readOnlyInput
            placeholder="Click to select dates"
            disabled={isLoading}
          />
        </div>
      </div>
      <div className={docSignReqStyles.filterItemSearch}>
        <div className={docSignReqStyles.label}>Search:</div>
        <div>
          <InputText
            value={searchPhrase}
            onChange={(e) => {
              setSearchPhrase(e.target.value)
            }}
            className={docSignReqStyles.searchText}
            placeholder="Type to search"
            disabled={isLoading}
          />
        </div>
        <div className={docSignReqStyles.filterHelpText}>
          Search on: Title, Document(s) or Metadata
        </div>
      </div>
      <div className={docSignReqStyles.filterItemSort}>
        <div className={docSignReqStyles.label}>Sort documents:</div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Dropdown
            value={sortField}
            options={sortOptions}
            optionLabel="display"
            onChange={(e) => {
              const newSort = {
                sort: e.value,
                sortDirection: sortDirection.value,
              }
              setSortField(e.value)
              onChangeSortField(newSort)
            }}
            style={{ width: "100%", margin: "0px" }}
            disabled={isLoading}
          />
          <button
            className={`btn ${docSignStyles.sortDirection} tt-ds-requests-filter`}
            onClick={() => {
              const newDirection =
                sortDirection.value === sortDirections[0].value
                  ? sortDirections[1]
                  : sortDirections[0]
              const newSort = {
                sort: sortField,
                sortDirection: newDirection.value,
              }

              setSortDirection(newDirection)
              onChangeSortField(newSort)
            }}
            data-pr-tooltip={`${sortDirection.value}, click to change to ${
              sortDirection.value === sortDirections[0].value
                ? sortDirections[1].value
                : sortDirections[0].value
            }`}
            data-pr-position="bottom"
            disabled={isLoading}
          >
            <span className="material-icons ">{sortDirection.icon}</span>
          </button>
        </div>
      </div>

      <div className={docSignReqStyles.filterItemStatus}>
        <div className={docSignReqStyles.label}>Filter by Status:</div>
        <div>
          <Dropdown
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.value)
            }}
            className={docSignReqStyles.status}
            options={statusOptions}
            optionLabel="name"
            optionValue="id"
            disabled={isLoading}
          />
        </div>
      </div>
      <div className={docSignReqStyles.filterItemStaff}>
        <div className={docSignReqStyles.label}>Filter by Staff:</div>
        <div>
          <MultiSelect
            value={staffFilter}
            placeholder="All Staff"
            onChange={(e) => {
              setStaffFilter(e.value)
            }}
            className={docSignReqStyles.status}
            options={staffOptions}
            optionLabel="name"
            filter
            disabled={isLoading}
            maxSelectedLabels={maxFilterSelection}
          />
        </div>
        {staffFilter.length > maxFilterSelection ? (
          <label className={formStyles.errFieldMsg}>
            Only {maxFilterSelection} selected staff entries are allowed at a
            time
          </label>
        ) : null}
      </div>
      <div className={docSignReqStyles.filterItemParticipant}>
        <div className={docSignReqStyles.label}>Filter by Participant:</div>
        <div>
          <MultiSelect
            value={participantFilter}
            placeholder="All Participants"
            onChange={(e) => {
              if (
                e.value.length === 0 ||
                (e.value.length === 1 && e.value[0] === allParticipants) ||
                (e.value.some(
                  (participant) =>
                    participant.participant_id ===
                    allParticipants.participant_id
                ) &&
                  !participantFilter.some(
                    (participant) =>
                      participant.participant_id ===
                      allParticipants.participant_id
                  ))
              ) {
                setParticipantFilter([allParticipants])
              } else {
                setParticipantFilter(
                  e.value.filter(
                    (participant) =>
                      participant.participant_id !==
                      allParticipants.participant_id
                  )
                )
              }
            }}
            className={docSignReqStyles.status}
            options={participantOptions}
            optionLabel="name"
            filter
            disabled={isLoading}
            maxSelectedLabels={3}
            selectionLimit={maxFilterSelection}
            showSelectAll={false}
          />
        </div>
        {participantFilter.length > maxFilterSelection ? (
          <label className={formStyles.errFieldMsg}>
            Only {maxFilterSelection} selected participants are allowed at a
            time
          </label>
        ) : null}
      </div>

      <div className={docSignReqStyles.filterItemButtons}>
        <button
          className={`btn`}
          type="button"
          onClick={() => {
            if (
              participantFilter.length <= maxFilterSelection &&
              staffFilter.length <= maxFilterSelection
            ) {
              onChangeFilters({
                dateCreated: dateFilter,
                search: searchPhrase,
                staff: staffFilter,
                participants: participantFilter,
                status: statusFilter,
                sort: sortField,
                sortDirection: sortDirection,
              })
            } else {
              userCtx.setNotificationError(
                `Participant and Staff selections may not exceed ${maxFilterSelection}, please update your filters`
              )
            }
          }}
          disabled={isLoading}
        >
          Apply
        </button>
        <button
          className={`btn ${docSignStyles.filterButton}`}
          type="button"
          onClick={() => {
            setSearchPhrase("")
            setParticipantFilter([allParticipants])
            setStaffFilter([])
            setDateFilter(null)
            setSortField(sortOptions[2])
            setSortDirection(sortDirections[0])
            onChangeFilters({
              dateCreated: null,
              search: "",
              staff: [],
              participants: [],
              status: "all",
              sort: sortOptions[3],
              sortDirection: sortDirections[0],
            })
            setStatusFilter("")
          }}
          disabled={isLoading}
        >
          Reset
        </button>
      </div>
    </div>
  )
}
export default RequestsFilters
